import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Input, Row, Col, Card, Tag, Collapse, Drawer, Spin, Empty, Typography
} from "antd";
import {
  SearchOutlined, QuestionCircleOutlined, BookOutlined, RocketOutlined,
  ArrowLeftOutlined, FileTextOutlined, FolderOutlined, HomeOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const FAQ_SECTION_ID = "faq-section";
const GENERIC_FAQ_PANEL_PREFIX = "generic-faq-";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #014c75 0%, #0369a1 100%);
  border-radius: 16px;
  padding: 40px 32px;
  margin-bottom: 32px;
  color: white;
  text-align: center;

  h2 { color: white; font-size: 28px; font-weight: 700; margin: 0 0 8px; }
  p { color: rgba(255,255,255,0.85); font-size: 15px; margin: 0 0 24px; }
`;

const SearchWrapper = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto;

  .search-input {
    border-radius: 12px;
    height: 48px;
    font-size: 15px;
    padding-left: 44px;
  }

  .search-dropdown {
    position: absolute;
    top: 52px;
    left: 0; right: 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    z-index: 1000;
    max-height: 360px;
    overflow-y: auto;
  }

  .search-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    transition: background 0.15s;

    &:last-child { border-bottom: none; }
    &:hover { background: #f0f9ff; }

    .item-icon { font-size: 16px; color: #0369a1; margin-top: 2px; flex-shrink: 0; }
    .item-title { font-weight: 500; color: #1e293b; font-size: 14px; }
    .item-type { font-size: 11px; color: #94a3b8; text-transform: uppercase; margin-top: 2px; }
  }

  .search-empty { padding: 20px; text-align: center; color: #94a3b8; font-size: 14px; }
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .anticon { color: #0369a1; }
`;

const CategoryCard = styled(Card)`
  cursor: pointer;
  border-radius: 12px;
  border: 2px solid ${props => props.active ? '#014c75' : '#e2e8f0'};
  background: ${props => props.active ? '#f0f9ff' : 'white'};
  transition: all 0.2s;
  text-align: center;
  height: 100%;

  &:hover {
    border-color: #014c75;
    box-shadow: 0 4px 12px rgba(1, 76, 117, 0.12);
    transform: translateY(-2px);
  }

  .anticon { font-size: 28px; color: ${props => props.active ? '#014c75' : '#64748b'}; margin-bottom: 8px; }
  .cat-name { font-weight: 600; color: ${props => props.active ? '#014c75' : '#1e293b'}; font-size: 13px; }
  .cat-count { font-size: 12px; color: #94a3b8; margin-top: 4px; }
  .ant-card-body { padding: 20px 12px; }
`;

const ArticleCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
  height: 100%;

  &:hover {
    border-color: #0369a1;
    box-shadow: 0 4px 16px rgba(3, 105, 161, 0.1);
    transform: translateY(-2px);
  }

  .art-title { font-weight: 600; color: #1e293b; margin-bottom: 8px; font-size: 14px; }
  .art-preview { color: #64748b; font-size: 13px; line-height: 1.5; }
  .art-meta { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
  .ant-card-body { padding: 20px; }
`;

const ICON_MAP = {
  QuestionCircleOutlined: <QuestionCircleOutlined />,
  BookOutlined: <BookOutlined />,
  RocketOutlined: <RocketOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  FolderOutlined: <FolderOutlined />,
};

const getIcon = (iconName) => ICON_MAP[iconName] || <QuestionCircleOutlined />;

const HelpCenter = ({ forceRole } = {}) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [gettingStarted, setGettingStarted] = useState([]);
  const [genericFaqs, setGenericFaqs] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [catLoading, setCatLoading] = useState(false);
  const [activeFaqKey, setActiveFaqKey] = useState(null);
  const [activeCatFaqKey, setActiveCatFaqKey] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const faqSectionRef = useRef(null);

  const token = localStorage.getItem("authToken");

  const roleMatches = useCallback((visibleTo) => {
    if (!forceRole) return true;
    return visibleTo === forceRole || visibleTo === "both";
  }, [forceRole]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(API_CONST.HELP_LIST_CATEGORIES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const cats = forceRole
          ? res.data.categories.filter(c => roleMatches(c.visibleTo))
          : res.data.categories;
        setCategories(cats);
      }
    } catch (e) { console.error(e); }
  }, [token, forceRole, roleMatches]);

  const fetchGettingStarted = useCallback(async () => {
    try {
      const res = await axios.post(API_CONST.HELP_LIST_ITEMS, {
        type: "article", isGettingStarted: true, limit: 50
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        const items = forceRole
          ? res.data.items.filter(i => roleMatches(i.visibleTo) && i.status === "published")
          : res.data.items;
        setGettingStarted(items.slice(0, 6));
      }
    } catch (e) { console.error(e); }
  }, [token, forceRole, roleMatches]);

  const fetchGenericFaqs = useCallback(async () => {
    try {
      const res = await axios.post(API_CONST.HELP_LIST_ITEMS, {
        type: "faq", categoryId: "generic", limit: 50
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        const items = forceRole
          ? res.data.items.filter(i => roleMatches(i.visibleTo) && i.status === "published")
          : res.data.items;
        setGenericFaqs(items);
      }
    } catch (e) { console.error(e); }
  }, [token, forceRole, roleMatches]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCategories(), fetchGettingStarted(), fetchGenericFaqs()])
      .finally(() => setLoading(false));
  }, [fetchCategories, fetchGettingStarted, fetchGenericFaqs]);

  useEffect(() => {
    if (!selectedCategory) return;
    setCatLoading(true);
    axios.post(API_CONST.HELP_LIST_ITEMS, {
      categoryId: selectedCategory.id, limit: 50
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data.success) {
          const items = forceRole
            ? res.data.items.filter(i => roleMatches(i.visibleTo) && i.status === "published")
            : res.data.items;
          setCategoryItems(items);
        }
      })
      .catch(console.error)
      .finally(() => setCatLoading(false));
  }, [selectedCategory, token, forceRole, roleMatches]);

  const handleSearch = useCallback((val) => {
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(API_CONST.HELP_SEARCH_ITEMS, {
          params: { query: val, limit: 8 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          const items = forceRole
            ? res.data.items.filter(i => roleMatches(i.visibleTo))
            : res.data.items;
          setSearchResults(items);
          setShowDropdown(true);
        }
      } catch (e) { console.error(e); }
      finally { setSearchLoading(false); }
    }, 300);
  }, [token, forceRole, roleMatches]);

  const openItem = (item) => {
    setShowDropdown(false);
    setSearchQuery("");
    if (item.type === "article") {
      setSelectedArticle(item);
      setDrawerOpen(true);
    } else if (item.type === "faq") {
      if (item.categoryId) {
        const cat = categories.find(c => c.id === item.categoryId);
        if (cat) {
          setSelectedCategory(cat);
          setTimeout(() => setActiveCatFaqKey(item.id), 300);
        } else {
          setSelectedCategory(null);
          setActiveFaqKey(item.id);
          setTimeout(() => {
            faqSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }
      } else {
        setSelectedCategory(null);
        setActiveFaqKey(item.id);
        setTimeout(() => {
          faqSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  };

  const handleClickOutside = useCallback((e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const renderContent = (content) => {
    if (!content) return null;
    return content.split("\n").map((line, i) => {
      if (line.startsWith("## ")) {
        return <Title level={4} key={i} style={{ marginTop: 20, color: "#014c75" }}>{line.slice(3)}</Title>;
      }
      if (line.startsWith("- ") || line.startsWith("• ")) {
        return <li key={i} style={{ marginLeft: 16, marginBottom: 4, color: "#374151" }}>{line.slice(2)}</li>;
      }
      if (/^\d+\. /.test(line)) {
        return <p key={i} style={{ margin: "4px 0", color: "#374151" }}>{line}</p>;
      }
      if (line.trim() === "") return <br key={i} />;
      return <Paragraph key={i} style={{ margin: "4px 0", color: "#374151" }}>{line}</Paragraph>;
    });
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "80px 0" }}><Spin size="large" /></div>;
  }

  const catArticles = categoryItems.filter(i => i.type === "article");
  const catFaqs = categoryItems.filter(i => i.type === "faq");

  return (
    <div>
      {!forceRole && (
        <PageHeader>
          <h1>Help Center</h1>
          <p>Find answers, guides, and documentation for the HireNXT platform</p>
        </PageHeader>
      )}

      <HeroSection>
        <h2>How can we help you?</h2>
        <p>Search our knowledge base or browse categories below</p>
        <SearchWrapper ref={searchRef}>
          <Input
            prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.7)", marginLeft: 4 }} />}
            placeholder="Search articles, FAQs, and guides..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            className="search-input"
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "white" }}
            allowClear
            onClear={() => { setSearchQuery(""); setSearchResults([]); setShowDropdown(false); }}
          />
          {showDropdown && (
            <div className="search-dropdown">
              {searchLoading ? (
                <div style={{ padding: "16px", textAlign: "center" }}><Spin size="small" /></div>
              ) : searchResults.length === 0 ? (
                <div className="search-empty">No results found for "{searchQuery}"</div>
              ) : searchResults.map(item => (
                <div key={item.id} className="search-item" onClick={() => openItem(item)}>
                  <span className="item-icon">{item.type === "article" ? <FileTextOutlined /> : <QuestionCircleOutlined />}</span>
                  <div>
                    <div className="item-title">{item.title}</div>
                    <div className="item-type">{item.type === "faq" ? "FAQ" : "Article"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SearchWrapper>
      </HeroSection>

      {categories.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          {selectedCategory ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div
                style={{ cursor: "pointer", padding: "6px 14px", background: "#f1f5f9", borderRadius: 8, fontWeight: 500, color: "#64748b", display: "flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", flexShrink: 0 }}
                onClick={() => setSelectedCategory(null)}
              >
                <ArrowLeftOutlined /> All Categories
              </div>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>›</div>
              <div style={{ fontWeight: 600, color: "#014c75", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                {getIcon(selectedCategory.icon)} {selectedCategory.name}
              </div>
            </div>
          ) : (
            <SectionTitle><FolderOutlined /> Browse by Category</SectionTitle>
          )}
          <Row gutter={[12, 12]}>
            {categories.map(cat => (
              <Col xs={12} sm={8} md={6} lg={4} key={cat.id}>
                <CategoryCard
                  active={selectedCategory?.id === cat.id ? 1 : 0}
                  onClick={() => setSelectedCategory(selectedCategory?.id === cat.id ? null : cat)}
                >
                  <div className="anticon">{getIcon(cat.icon)}</div>
                  <div className="cat-name">{cat.name}</div>
                </CategoryCard>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {selectedCategory ? (
        <div>
          {selectedCategory.description && (
            <div style={{ color: "#64748b", fontSize: 14, marginBottom: 24, padding: "12px 16px", background: "#f8fafc", borderRadius: 8, borderLeft: "3px solid #0369a1" }}>
              {selectedCategory.description}
            </div>
          )}

          {catLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}><Spin /></div>
          ) : (
            <div>
              {catArticles.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <SectionTitle><BookOutlined /> Articles</SectionTitle>
                  <Row gutter={[12, 12]}>
                    {catArticles.map(art => (
                      <Col xs={24} sm={12} key={art.id}>
                        <ArticleCard onClick={() => openItem(art)}>
                          <div className="art-title"><FileTextOutlined style={{ marginRight: 6, color: "#0369a1" }} />{art.title}</div>
                          <div className="art-preview">{art.content?.slice(0, 100)}...</div>
                          <div className="art-meta">
                            {art.isGettingStarted && <Tag color="green">Getting Started</Tag>}
                            {art.tags?.map(t => <Tag key={t}>{t}</Tag>)}
                          </div>
                        </ArticleCard>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {catFaqs.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <SectionTitle><QuestionCircleOutlined /> Frequently Asked Questions</SectionTitle>
                  <Collapse
                    accordion
                    activeKey={activeCatFaqKey}
                    onChange={key => setActiveCatFaqKey(key)}
                    style={{ borderRadius: 12, background: "white" }}
                    expandIconPosition="end"
                  >
                    {catFaqs.map(faq => (
                      <Panel
                        header={<span style={{ fontWeight: 500, color: "#1e293b" }}>{faq.title}</span>}
                        key={faq.id}
                        style={{ borderRadius: 8 }}
                      >
                        <Paragraph style={{ color: "#374151", lineHeight: 1.7, margin: 0 }}>
                          {faq.content}
                        </Paragraph>
                      </Panel>
                    ))}
                  </Collapse>
                </div>
              )}

              {catArticles.length === 0 && catFaqs.length === 0 && (
                <Empty description="No content in this category yet" style={{ padding: "40px 0" }} />
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {gettingStarted.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <SectionTitle><RocketOutlined /> Getting Started</SectionTitle>
              <Row gutter={[12, 12]}>
                {gettingStarted.map(art => (
                  <Col xs={24} sm={12} md={8} key={art.id}>
                    <ArticleCard onClick={() => openItem(art)}>
                      <div className="art-title"><FileTextOutlined style={{ marginRight: 6, color: "#0369a1" }} />{art.title}</div>
                      <div className="art-preview">{art.content?.slice(0, 100)}...</div>
                    </ArticleCard>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {genericFaqs.length > 0 && (
            <div ref={faqSectionRef} id={FAQ_SECTION_ID}>
              <SectionTitle><QuestionCircleOutlined /> Frequently Asked Questions</SectionTitle>
              <Collapse
                accordion
                activeKey={activeFaqKey}
                onChange={key => setActiveFaqKey(key)}
                style={{ borderRadius: 12, background: "white" }}
                expandIconPosition="end"
              >
                {genericFaqs.map(faq => (
                  <Panel
                    header={<span style={{ fontWeight: 500, color: "#1e293b" }}>{faq.title}</span>}
                    key={faq.id}
                    style={{ borderRadius: 8 }}
                  >
                    <Paragraph style={{ color: "#374151", lineHeight: 1.7, margin: 0 }}>
                      {faq.content}
                    </Paragraph>
                  </Panel>
                ))}
              </Collapse>
            </div>
          )}

          {gettingStarted.length === 0 && genericFaqs.length === 0 && categories.length === 0 && (
            <Empty
              image={<QuestionCircleOutlined style={{ fontSize: 60, color: "#cbd5e1" }} />}
              description="Help content is being prepared. Check back soon!"
              style={{ padding: "60px 0" }}
            />
          )}
        </>
      )}

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FileTextOutlined style={{ color: "#0369a1" }} />
            <span>{selectedArticle?.title}</span>
          </div>
        }
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedArticle(null); }}
        width={640}
        styles={{ body: { padding: "24px 32px" } }}
      >
        {selectedArticle && (
          <div>
            <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {selectedArticle.isGettingStarted && <Tag color="green">Getting Started</Tag>}
              {selectedArticle.tags?.map(t => <Tag key={t}>{t}</Tag>)}
            </div>
            <div style={{ lineHeight: 1.8 }}>
              {renderContent(selectedArticle.content)}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default HelpCenter;
